import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConfigKey } from '@/types';

import FilterIcon from '@/assets/icons/filter.svg?react';
import ConfigSection from '@/components/map/configuration-button/config-section';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import TooltipPopover from '@/components/ui/tooltip-popover';
import useBreakpoint from '@/hooks/use-break-point';
import { useMapConfig } from '@/hooks/use-map-config';
import usePinnedItem from '@/hooks/use-pinned-item';
import { cn } from '@/lib/utils';
import { GENERAL_OPTIONS, INITIAL_MAP_CONFIG, TRAIN_CATEGORY_OPTIONS } from '@/utils/constants/map';
import isLandscape from '@/utils/helpers/is-landscape';

const ConfigurationButton: FC = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const { pinnedItemId, pinnedItemType } = usePinnedItem();
  const isMobile = useBreakpoint('mobile');

  const isTrainPinned = pinnedItemId && pinnedItemType === 'route';
  const isInteractive = !isTrainPinned;

  const { config, handleToggle, handleReset } = useMapConfig();
  const landscape = isLandscape();

  const isConfigModified = useMemo(() => {
    return Object.keys(INITIAL_MAP_CONFIG).some(
      (key) =>
        key !== 'hideDetails' && config[key as ConfigKey] !== INITIAL_MAP_CONFIG[key as ConfigKey]
    );
  }, [config]);

  return (
    <div className="flex flex-col">
      <TooltipPopover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        tooltipLabel={t('map.actions.configuration.title')}
        tooltipSide="right"
        triggerContent={<FilterIcon className="size-6 fill-current text-current" />}
        triggerClassName="size-10 p-0"
        triggerName="map-configuration"
        triggerAriaLabel="Customize map"
        triggerDisabled={!isInteractive}
        contentSide={landscape ? 'right' : 'bottom'}
        contentAlign="end"
        contentSideOffset={isMobile ? -24 : 4}
        contentCollisionPadding={16}
        contentClassName="w-82 p-4 rounded-lg border-none shadow-md flex flex-col gap-4 overflow-y-auto max-h-[calc(100dvh-6rem)] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        <div className="flex justify-between items-center shrink-0">
          <h3 className="font-semibold text-base text-foreground">
            {t('map.actions.configuration.title')}
          </h3>
          <Button
            variant="ghost"
            onClick={handleReset}
            disabled={!isConfigModified}
            name="reset-map-configuration"
            aria-label="Reset map customization"
            className={cn(
              'h-auto p-0 hover:bg-transparent text-xs transition-colors text-muted-foreground',
              { 'text-foreground cursor-pointer': isConfigModified }
            )}
          >
            {t('map.actions.configuration.reset')}
          </Button>
        </div>

        <div className="flex flex-col gap-4">
          <ConfigSection
            title={t('map.actions.configuration.train_category.title')}
            options={TRAIN_CATEGORY_OPTIONS}
            config={config}
            onToggle={handleToggle}
          />

          <Separator className="bg-border shrink-0" />

          <ConfigSection
            title={t('map.actions.configuration.general.title')}
            options={GENERAL_OPTIONS}
            config={config}
            onToggle={handleToggle}
          />
        </div>
      </TooltipPopover>
    </div>
  );
};

export default ConfigurationButton;
